import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from 'components/ui/button';
import { diaEMes, PrazoDaFase } from 'pages/RealizacaoDestaque/components/ChamadaDeCadastro';
import { FOCO_VISIVEL } from 'utils/foco';

import { EIXOS_TEMATICOS, EstadoDoCadastroEcoa } from '../acoes-do-semestre';

/** ECN-22: os três cartões de pontuação do design, sem limite mensal de cadastro. */
const PONTUACAO = [
  { titulo: 'Ação validada', valor: '100 pontos', texto: 'Ciência encerra a ação sem pontos. Até 1 ação validada por mês na dependência.' },
  { titulo: 'Ação bonificada', valor: '300 pontos', texto: '100 da validação + 200 da bonificação. Até 1 ação por semestre.' },
  { titulo: 'Seus tetos', valor: '700 no semestre', texto: 'Limitado a 100 pontos por mês e 300 por ação bonificada.' },
];

export interface PainelDaDinamicaProps {
  /** `estadoDoCadastro`; `null` enquanto o horário do servidor não chegou. */
  estado: EstadoDoCadastroEcoa | null;
  /** Horário do servidor, em milissegundos. */
  agora: number;
  onIrParaCicloAtual: (cicloId: string) => void;
}

function ChamadaDoCadastro({ estado, agora, onIrParaCicloAtual }: PainelDaDinamicaProps & { estado: EstadoDoCadastroEcoa }) {
  const navigate = useNavigate();

  switch (estado.tipo) {
    case 'aberto':
      return (
        <>
          <PrazoDaFase fase={estado.fase} agora={agora} />
          <div className="mt-4">
            <Button variant="solido-destaque" data-abre-cadastro onClick={() => navigate('/acao-ecoa/cadastrar')}>
              Cadastrar Ação ECOA
            </Button>
          </div>
        </>
      );

    case 'indisponivel':
      return <PrazoDaFase fase={estado.fase} agora={agora} />;

    case 'em-outro-ciclo':
      return (
        <div className="flex flex-wrap items-center gap-3.5">
          <p className="m-0 flex-[1_1_240px] text-[12.5px] leading-[1.55] text-content">
            {`O cadastramento aberto agora é o da fase ${estado.fase.title} do Ciclo ${estado.ciclo.title}.`}
          </p>
          <Button variant="solido-destaque" onClick={() => onIrParaCicloAtual(estado.ciclo._id)}>
            Ir para o ciclo atual
          </Button>
        </div>
      );

    case 'fechado':
      return (
        <p className="m-0 text-[12.5px] leading-[1.55] text-content">
          O cadastramento está fechado.
          {estado.proximaAbertura !== undefined && ` O próximo abre em ${diaEMes(estado.proximaAbertura)}.`}
        </p>
      );
  }
}

/** ECN-28: bloco recolhível com os quatro eixos do cadastro. */
function EixosTematicos() {
  const [aberto, setAberto] = useState(false);
  const listaId = useId();

  return (
    <div className="mt-4 border-t border-[rgba(28,28,92,0.15)] pt-3.5">
      <h3 className="m-0">
        <button
          type="button"
          aria-expanded={aberto}
          aria-controls={listaId}
          onClick={() => setAberto(atual => !atual)}
          className={`flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 font-display text-[13px] font-bold text-brand-blue-dark ${FOCO_VISIVEL}`}
        >
          <span aria-hidden="true">{aberto ? '−' : '+'}</span>
          Eixos temáticos da Ação ECOA
        </button>
      </h3>
      <ul id={listaId} hidden={!aberto} className="m-0 mt-2.5 flex list-none flex-wrap gap-2 p-0">
        {EIXOS_TEMATICOS.map(eixo => (
          <li key={eixo} className="rounded-jj-pill bg-brand-white px-3 py-[5px] text-[11.5px] font-bold text-brand-blue-dark">
            {eixo}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Painel verde do topo do integrante (ECN-22 a ECN-26, ECN-28): como funciona, pontuação, cadastramento e eixos. */
export default function PainelDaDinamica(props: PainelDaDinamicaProps) {
  const { estado } = props;

  return (
    <section aria-labelledby="titulo-como-funciona-ecoa" className="rounded-jj-panel bg-brand-green px-[30px] py-[26px]">
      <h2 id="titulo-como-funciona-ecoa" className="m-0 mb-2.5 font-display text-[19px] font-bold text-brand-blue-dark">
        Como funciona a dinâmica Ação ECOA?
      </h2>
      <p className="m-0 mb-2.5 text-[13px] leading-[1.65] text-brand-navy">
        É uma ação contínua de engajamento para reconhecer e dar visibilidade às ações realizadas pelas Ecoas.
      </p>
      <p className="m-0 mb-4 text-[13px] leading-[1.65] text-brand-navy">
        Os integrantes da Ecoa cadastram as ações e coordenador ou tutor pode dar ciência, validar e bonificar.
      </p>

      <ul aria-label="Pontuação da Ação ECOA" className="m-0 mb-4 grid list-none grid-cols-1 gap-2.5 p-0 md:grid-cols-3">
        {PONTUACAO.map(cartao => (
          <li key={cartao.titulo} className="rounded-jj-lg bg-brand-white px-4 py-3.5">
            <p className="m-0 text-[11px] font-bold uppercase tracking-[0.04em] text-content">{cartao.titulo}</p>
            <p className="m-0 mt-1 font-display text-lg font-bold text-brand-blue-dark">{cartao.valor}</p>
            <p className="m-0 mt-1 text-[11.5px] leading-[1.5] text-content">{cartao.texto}</p>
          </li>
        ))}
      </ul>

      {estado && (
        <div className="rounded-jj-lg bg-brand-white px-[18px] py-4">
          <ChamadaDoCadastro {...props} estado={estado} />
        </div>
      )}

      <EixosTematicos />
    </section>
  );
}
